import React, { useCallback, useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { useTheme } from 'hooks/useTheme';
import { trackEvent } from 'utils/analytics';
import config from 'data/config';

interface PostNode {
  title: string;
  slug: string;
  date: string;
}

interface ProjectNode {
  title: string;
  slug: string;
}

interface SearchData {
  allWpPost: { nodes: PostNode[] };
  allWpProject: { nodes: ProjectNode[] };
}

/**
 * Cmd+K command palette with blog/project search, navigation, and actions.
 * Uses cmdk for fuzzy filtering and keyboard navigation.
 * Mounts globally via gatsby-browser wrapRootElement.
 */
export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const data = useStaticQuery<SearchData>(graphql`
    query CommandPaletteSearch {
      allWpPost(sort: { date: DESC }, limit: 100) {
        nodes {
          title
          slug
          date(formatString: "MMM DD, YYYY")
        }
      }
      allWpProject(sort: { date: DESC }, limit: 50) {
        nodes {
          title
          slug
        }
      }
    }
  `);

  // Toggle on Cmd+K / Ctrl+K
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    const onOpenRequest = () => setOpen(true);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('open-command-palette', onOpenRequest);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('open-command-palette', onOpenRequest);
    };
  }, []);

  const go = useCallback((path: string) => {
    setOpen(false);
    navigate(path);
    trackEvent('cmd_palette_navigate', { path });
  }, []);

  const handleThemeToggle = useCallback(() => {
    toggleTheme();
    setOpen(false);
    trackEvent('cmd_palette_action', { action: 'toggle_theme' });
  }, [toggleTheme]);

  const handleCopyUrl = useCallback(async () => {
    if (typeof window !== 'undefined') {
      await navigator.clipboard.writeText(window.location.href);
      trackEvent('cmd_palette_action', { action: 'copy_url' });
    }
    setOpen(false);
  }, []);

  if (!open) return null;

  return (
    <div className="cmd-palette-overlay" onClick={() => setOpen(false)}>
      <div
        className="cmd-palette-container"
        onClick={(e) => e.stopPropagation()}
      >
        <Command label="Command palette" className="cmd-palette">
          <Command.Input
            placeholder="Search posts, projects, or type a command…"
            className="cmd-palette-input"
            autoFocus
          />
          <Command.List className="cmd-palette-list">
            <Command.Empty className="cmd-palette-empty">
              No results found.
            </Command.Empty>

            {/* Navigation */}
            <Command.Group heading="Navigate" className="cmd-palette-group">
              <Command.Item
                onSelect={() => go('/')}
                className="cmd-palette-item"
              >
                <span className="cmd-icon">⌂</span> Home
              </Command.Item>
              <Command.Item
                onSelect={() => go(`/${config.routes.posts}/`)}
                className="cmd-palette-item"
              >
                <span className="cmd-icon">✎</span> All Posts
              </Command.Item>
              <Command.Item
                onSelect={() => go(`/${config.routes.projects}/`)}
                className="cmd-palette-item"
              >
                <span className="cmd-icon">◈</span> Projects
              </Command.Item>
              <Command.Item
                onSelect={() => go(`/${config.routes.tags}/`)}
                className="cmd-palette-item"
              >
                <span className="cmd-icon">#</span> Tags
              </Command.Item>
              <Command.Item
                onSelect={() => go('/#contact')}
                className="cmd-palette-item"
              >
                <span className="cmd-icon">✉</span> Contact
              </Command.Item>
            </Command.Group>

            {/* Actions */}
            <Command.Group heading="Actions" className="cmd-palette-group">
              <Command.Item
                onSelect={handleThemeToggle}
                className="cmd-palette-item"
              >
                <span className="cmd-icon">{theme === 'dark' ? '☀' : '☾'}</span>
                Toggle {theme === 'dark' ? 'Light' : 'Dark'} Mode
              </Command.Item>
              <Command.Item
                onSelect={handleCopyUrl}
                className="cmd-palette-item"
              >
                <span className="cmd-icon">⎘</span> Copy Current URL
              </Command.Item>
              <Command.Item
                onSelect={() => {
                  window.open(
                    'https://github.com/gfargo/griffen.codes',
                    '_blank'
                  );
                  setOpen(false);
                }}
                className="cmd-palette-item"
              >
                <span className="cmd-icon">⟁</span> View on GitHub
              </Command.Item>
            </Command.Group>

            {/* Posts */}
            {data.allWpPost.nodes.length > 0 && (
              <Command.Group heading="Posts" className="cmd-palette-group">
                {data.allWpPost.nodes.map((post) => (
                  <Command.Item
                    key={post.slug}
                    value={`post ${post.title}`}
                    onSelect={() => go(`/${config.routes.post}/${post.slug}/`)}
                    className="cmd-palette-item"
                  >
                    <span className="cmd-icon">→</span>
                    <span className="cmd-item-title">{post.title}</span>
                    <span className="cmd-item-meta">{post.date}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Projects */}
            {data.allWpProject.nodes.length > 0 && (
              <Command.Group heading="Projects" className="cmd-palette-group">
                {data.allWpProject.nodes.map((project) => (
                  <Command.Item
                    key={project.slug}
                    value={`project ${project.title}`}
                    onSelect={() =>
                      go(`/${config.routes.projects}/${project.slug}/`)
                    }
                    className="cmd-palette-item"
                  >
                    <span className="cmd-icon">◆</span>
                    <span className="cmd-item-title">{project.title}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
        </Command>

        <div className="cmd-palette-footer">
          <span>
            <kbd>↑↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> select
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
